import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SectionsProvider } from './contexts/SectionsContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { CleanerLayout } from './components/CleanerLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { PropertiesPage } from './pages/PropertiesPage';
import PropertyDetailsPage from './pages/PropertyDetailsPage';
import UnitDetailsPage from './pages/UnitDetailsPage';
import BookingsPage from './pages/BookingsPage';
import UsersPage from './pages/UsersPage';
import ProfilePage from './pages/ProfilePage';
import CurrenciesPage from './pages/CurrenciesPage';
import AccountingPage from './pages/AccountingPage';
import CleanerPage from './pages/CleanerPage';
import { SettingsPage } from './pages/SettingsPage';
import { DatabaseAdminPage } from './pages/DatabaseAdminPage';
import PaymentVouchersPage from './pages/PaymentVouchersPage';
//import AccountingPage from './pages/AccountingPage';
import TestRTLPage from './pages/TestRTLPage';
import { useAndroidBackButton } from './hooks/useAndroidBackButton';
import './i18n'; // Initialize i18n

// Create a client
const queryClient = new QueryClient();

// Refresh prevention mechanism
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 2000; // 2 seconds

const preventRapidRefresh = () => {
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_COOLDOWN) {
    console.warn('Rapid refresh detected. Please wait a moment before refreshing again.');
    return false;
  }
  lastRefreshTime = now;
  return true;
};

// Add event listener for page refresh
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (!preventRapidRefresh()) {
      e.preventDefault();
      e.returnValue = 'Please wait a moment before refreshing the page.';
    }
  });
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If user is cleaner, redirect to cleaner page unless already there
  if (user.role === 'cleaner' && !window.location.pathname.startsWith('/cleaner')) {
    return <Navigate to="/cleaner" replace />;
  }

  return <>{children}</>;
};

// Cleaner-only Route Component
const CleanerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Only cleaners can access this route
  if (user.role !== 'cleaner') {
    return <Navigate to="/bookings" />;
  }

  return <>{children}</>;
};

// Admin/Staff Route Component (not for cleaners)
const NonCleanerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // If user is cleaner, redirect to cleaner page
  if (user.role === 'cleaner') {
    return <Navigate to="/cleaner" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to appropriate page if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (user) {
    // Redirect cleaners to cleaner page, others to bookings
    return user.role === 'cleaner' ? <Navigate to="/cleaner" /> : <Navigate to="/bookings" />;
  }

  return <>{children}</>;
};

// Mounted inside <Router> so it can wire the Android hardware back button
// to router history.
const AndroidBackButtonHandler: React.FC = () => {
  useAndroidBackButton();
  return null;
};

// Redirect component for catch-all route
const CleanerRedirect: React.FC = () => {
  const { user } = useAuth();
  
  if (user?.role === 'cleaner') {
    return <Navigate to="/cleaner" replace />;
  }
  
  return <Navigate to="/bookings" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <SidebarProvider>
          <SectionsProvider>
            <CssBaseline />
            <ErrorBoundary>
              <AuthProvider>
          <Router>
            <AndroidBackButtonHandler />
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />

              {/* Cleaner Routes */}
              <Route
                path="/cleaner"
                element={
                  <CleanerRoute>
                    <CleanerLayout />
                  </CleanerRoute>
                }
              >
                <Route index element={<CleanerPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>

              {/* Admin/Staff Routes */}
              <Route
                path="/"
                element={
                  <NonCleanerRoute>
                    <Layout />
                  </NonCleanerRoute>
                }
              >
                <Route index element={<Navigate to="/bookings" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="properties" element={<PropertiesPage />} />
                <Route path="properties/:id" element={<PropertyDetailsPage />} />
                <Route path="units/:id" element={<UnitDetailsPage />} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="currencies" element={<CurrenciesPage />} />
                <Route path="accounting" element={<AccountingPage />} />
                <Route path="payment-vouchers" element={<PaymentVouchersPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="database-admin" element={<DatabaseAdminPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="test-rtl" element={<TestRTLPage />} />
              </Route>
              
              {/* Catch all route */}
              <Route 
                path="*" 
                element={
                  <ProtectedRoute>
                    <CleanerRedirect />
                  </ProtectedRoute>
                } 
              />
            </Routes>
          </Router>
          </AuthProvider>
        </ErrorBoundary>
        </SectionsProvider>
        </SidebarProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
